'use client';

import { useObservable } from '@legendapp/state/react';
import { type ReactNode, type RefObject, createContext, useContext, useEffect, useRef } from 'react';
import { type Observable, ObservableHint, batch } from '@legendapp/state';
import { resize } from 'framer-motion';
import { cn } from '@/lib/cn';
import debounce from '@/lib/debounce';

export interface TabProviderProps {
    defaultTabId?: string;
    children: ReactNode;
}

interface TabState {
    activeTabId?: string;
    activeTab?: HTMLInputElement;
    indicator?: {
        top: number;
        left: number;
        width: number;
        enabled: () => boolean;
    };
}

const tabContext = createContext<Observable<TabState>>(undefined as unknown as Observable<TabState>);
export function TabProvider({ children, defaultTabId }: TabProviderProps) {
    const tabState$ = useObservable({
        activeTabId: defaultTabId,
        indicator: {
            enabled: () => Boolean(tabState$.indicator.left.get() !== undefined && tabState$.indicator.width.get() !== undefined),
        },
    } as TabState);
    const tabContainerRef = useRef<HTMLDivElement>(null);
    useInitTabIndicator({ tabState$, tabContainerRef });

    return (
        <tabContext.Provider value={tabState$}>
            <div className={cn('tabs pill group  [--tab-height:47px] whitespace-normal')} ref={tabContainerRef}>
                {children}
            </div>
        </tabContext.Provider>
    );
}

interface useInitResizeWatcherOptions {
    tabState$: Observable<TabState>;
    tabContainerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Initialize a resize watcher to update the tab indicator position and size when the window is resized or tabs are added/removed.
 * @param tabState$
 * @param tabContainerRef
 */
function useInitTabIndicator({ tabState$: tabStateIn$, tabContainerRef }: useInitResizeWatcherOptions) {
    return useEffect(() => {
        const container = tabContainerRef.current;
        if (!container) {
            return;
        }
        const tabState$ = tabStateIn$;
        const updateActiveTab = (input: HTMLInputElement) => {
            if (!input.checked) {
                return;
            }
            const tab = input.parentElement;
            if (!tab || !(tab instanceof HTMLLabelElement)) {
                return;
            }
            const tabTop = tab.offsetTop || 0;
            const tabLeft = tab.offsetLeft || 0;
            const tabWidth = tab.clientWidth || 0;

            requestAnimationFrame(() => {
                batch(() => {
                    tabState$.activeTab.set(ObservableHint.opaque(input));
                    tabState$.activeTabId.set(input.id);
                    tabState$.indicator.assign({
                        top: tabTop,
                        left: tabLeft,
                        width: tabWidth,
                    });
                });
            });
        };

        const handleChange = (event: Event) => {
            updateActiveTab(event.currentTarget as HTMLInputElement);
        };

        const tabs = container.querySelectorAll('.tab input') ?? [];
        tabs.forEach((tab) => tab.addEventListener('change', handleChange));

        const handleResize = debounce(() => {
            const activeTab = tabState$.activeTab.get();
            if (!activeTab) {
                return;
            }
            updateActiveTab(activeTab as HTMLInputElement);
        });

        const handleMutation: MutationCallback = (records) => {
            records.forEach((record) => {
                record.removedNodes.forEach((item) => {
                    if (item instanceof Element) {
                        resizeObserver.unobserve(item);
                    }
                });
                record.addedNodes.forEach((item) => {
                    if (item instanceof Element) {
                        resizeObserver.observe(item);
                    }
                });
            });
            handleResize();
        };

        const mutationObserver = new MutationObserver((records, mutation) => {
            handleMutation(records, mutation);
        });
        const resizeObserver = new ResizeObserver(() => handleResize());
        const parent = container.parentElement;
        if (parent) {
            mutationObserver.observe(parent, {
                childList: true,
            });
            for (const child of parent.children) {
                const isTab = child.classList.contains('tab');
                if (!isTab) {
                    continue;
                }
                resizeObserver.observe(child);
            }
        }
        const resizeUnsubscribe = resize(handleResize);

        const checkedTab = Array.from(tabs).find((tab) => (tab as HTMLInputElement).checked);
        const initialTab = checkedTab || tabs[0];
        if (initialTab) {
            updateActiveTab(initialTab as HTMLInputElement);
        }
        container.classList.add('has-indicator');
        return () => {
            resizeUnsubscribe();
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
            tabs.forEach((tab) => tab.removeEventListener('change', handleChange));
        };
    }, [tabContainerRef, tabStateIn$]);
}

export function useTabState() {
    const context = useContext(tabContext);
    if (!context) {
        throw new Error('useTabContext must be used within a TabProvider');
    }
    return context;
}
